import { jwtDecode } from "jwt-decode"

interface IJwtPayload {
  exp: number // Expiration time in seconds since epoch
  sub: string
}

/**
 * Decodes a JWT and returns its payload.
 * @param token The JWT token string.
 * @returns The decoded payload or null if decoding fails.
 */
const decodeJwt = (token: string): IJwtPayload | null => {
  try {
    return jwtDecode<IJwtPayload>(token)
  } catch {
    return null
  }
}

/**
 * Checks if the JWT token is about to expire within the threshold.
 * @param token The JWT token string.
 * @param thresholdSeconds The threshold in minutes (default is 15 minutes).
 * @returns True if the token expires within the threshold, else false.
 */
export const isTokenExpiring = (
  token: string,
  thresholdSeconds = 15,
): boolean => {
  const decoded = decodeJwt(token)
  if (!decoded?.exp) return true

  const currentTime = Date.now() / 1000
  const timeDiff = decoded.exp - currentTime
  const shouldExpire = timeDiff < thresholdSeconds * 60

  return shouldExpire
}

/**
 * Checks if a JWT is already expired.
 * Treats missing/invalid tokens as expired.
 */
export const isTokenExpired = (token?: string | null): boolean => {
  if (!token) return true
  return isTokenExpiring(token, 0)
}

export function extractUserID(token?: string) {
  if (!token) return null
  const decoded = decodeJwt(token)
  return decoded?.sub ?? null
}
